<template>
  <div v-if="hasParentNode" class="folder-create-inline my-1">
    <v-btn v-if="!isCreatingFolder"
      color="primary"
      variant="flat"
      size="small"
      height="32"
      @click="startCreatingFolder"
      :data-cy="`${dataCyPrefix}-create-folder-btn`"
    >
      <v-icon start>mdi-folder-plus</v-icon>
      {{ $t('menus.createNode.createFolder') }}
    </v-btn>
    <v-form
      v-else
      ref="form"
      @submit.prevent="submitCreateFolder"
      autocomplete="off"
    >
      <v-text-field
        v-model="folderName"
        :rules="folderNameRules"
        :placeholder="$t('menus.createNode.nodeName')"
        variant="solo-filled"
        density="compact"
        flat
        autofocus
        hide-details="auto"
        :data-cy="`${dataCyPrefix}-create-folder-input`"
        @keyup.enter="this.$refs.form.requestSubmit()"
        @keyup.esc="cancelCreatingFolder"
      >
        <template v-slot:append-inner>
          <v-tooltip location="bottom" :aria-label="$t('common.create')">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                type="submit"
                variant="text"
                icon
                size="small"
                height="32"
                width="32"
                :loading="isSubmitting"
                :disabled="isSubmitting"
                :data-cy="`${dataCyPrefix}-create-folder-confirm-btn`"
                :aria-label="$t('common.create')"
                @click.prevent="this.$refs.form.requestSubmit()"
              >
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('common.create') }}</span>
          </v-tooltip>

          <v-tooltip location="bottom" :aria-label="$t('common.cancel')">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                icon
                size="small"
                height="32"
                width="32"
                @click="cancelCreatingFolder"
                :disabled="isSubmitting"
                :data-cy="`${dataCyPrefix}-create-folder-cancel-btn`"
                :aria-label="$t('common.cancel')"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('common.cancel') }}</span>
          </v-tooltip>
        </template>
      </v-text-field>

      <div v-if="errorMessage" class="bg-error mt-2">
        {{ errorMessage }}
      </div>
    </v-form>
  </div>
</template>

<script>
import { useCommonStore } from '@/stores/CommonStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';

export default {
  name: 'FolderCreateInline',
  emits: ['created', 'cancel'],
  props: {
    parentNode: {
      type: Object,
      required: false,
      default: null,
    },
    dataCyPrefix: {
      type: String,
      required: false,
      default: 'folder-create-inline',
    },
  },
  data() {
    return {
      isCreatingFolder: false,
      folderName: '',
      errorMessage: '',
      isSubmitting: false,
      folderNameRules: [
        (v) => !!v || this.$t('routes.node.requiredName'),
        (v) => (v && v.length <= 191) || this.$t('routes.node.folderNameTooLong'),
      ],
    };
  },
  computed: {
    hasParentNode() {
      return !!this.parentNode?.id;
    },
  },
  methods: {
    startCreatingFolder() {
      if (!this.hasParentNode) {
        return;
      }

      this.errorMessage = '';
      this.folderName = '';
      this.isCreatingFolder = true;
    },
    cancelCreatingFolder() {
      this.isCreatingFolder = false;
      this.folderName = '';
      this.errorMessage = '';
      this.isSubmitting = false;
      this.$emit('cancel');
    },
    async submitCreateFolder() {
      if (!this.hasParentNode || this.isSubmitting) {
        return;
      }

      const form = this.$refs.form;
      if (!form) {
        return;
      }

      const { valid } = await form.validate();
      if (!valid) {
        return;
      }

      const folderName = this.folderName.trim();
      if (!folderName) {
        return;
      }

      const commonStore = useCommonStore();
      const nodeStore = useNodeStore();

      this.errorMessage = '';
      this.isSubmitting = true;

      try {
        const createdNode = await nodeStore.createNode({
          name: folderName,
          parentid: this.parentNode.id,
          type: commonStore.nodeTypes.FOLDER,
          teamonly: Boolean(this.parentNode.teamonly),
        });

        this.isCreatingFolder = false;
        this.folderName = '';
        this.$emit('created', createdNode);
      } catch (error) {
        this.errorMessage = this.$localizeMessage(
          error?.response?.data?.error,
          this.$t('backend.system-error'),
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.folder-create-inline {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
